import React, { useMemo } from "react";
import { useCurrencySelector } from "@appsinti/currency-provider";
import {
  formatMoney,
  formatMoneyMK,
  formatPercentage,
} from "@appsinti/common-utils/textFormat";
import StatsDay from "../../../../interfaces/StatsDay";
import CashCancellationsCourtesy from "./sections/CashCancellationsCourtesy";
import EmptyContainer from "../../../containers/EmptyContainer";
import { Ring } from "@uiball/loaders";
import Container from "./sections/Container";


const Day: React.FC<{
  stats: { [key: number]: StatsDay } | null;
  loading: boolean;
}> = ({ stats, loading }) => {
  const { currencyEnabled, currency } = useCurrencySelector();

  const data = useMemo(() => {
    if (!stats) {
      return {
        totalSales: null,
        salesPerGuest: null,
        cashTotal: null,
        cancellationsTotal: null,
        courtesyTotal: null,
        numberOfGroups: 0,
      };
    }

    if (currencyEnabled && currency) {
      const allValidGroups = Object.values(stats).filter((group) => {
        return !!group.salesData.conversions?.[currency];
      });
      if (allValidGroups.length > 0) {
        const response = {
          totalSales: 0,
          salesPerGuest: 0,
          cashTotal: 0,
          cancellationsTotal: 0,
          courtesyTotal: 0,
          numberOfGroups: allValidGroups.length,
        };
        for (const group of allValidGroups) {
          if (group.salesData.conversions?.[currency]) {
            response.totalSales +=
              group.salesData.conversions[currency].total.totalSales;
            response.salesPerGuest +=
              group.salesData.conversions[currency].total.salesPerGuest ?? 0;
            response.cashTotal +=
              group.salesData.conversions[currency].total.totalSales *
              (group.salesData.conversions[currency].total.cash / 100);
            response.cancellationsTotal +=
              group.salesData.conversions[currency].total.totalSales *
              (group.salesData.conversions[currency].total.cancellation / 100);
            response.courtesyTotal +=
              group.salesData.conversions[currency].total.totalSales *
              (group.salesData.conversions[currency].total.courtesy / 100);
          }
        }
        return response;
      }
    }

    const response = {
      totalSales: 0,
      salesPerGuest: 0,
      numberOfGroups: 0,
      cashTotal: 0,
      cancellationsTotal: 0,
      courtesyTotal: 0,
    };
    for (const group of Object.values(stats)) {
      if (
        group.salesData.total.totalSales &&
        group.salesData.total.totalSales > 0
      ) {
        response.totalSales += group.salesData.total.totalSales;
        response.salesPerGuest += group.salesData.total.salesPerGuest ?? 0;
        response.cashTotal +=
          group.salesData.total.totalSales * (group.salesData.total.cash / 100);
        response.cancellationsTotal +=
          group.salesData.total.totalSales *
          (group.salesData.total.cancellation / 100);
        response.courtesyTotal +=
          group.salesData.total.totalSales *
          (group.salesData.total.courtesy / 100);
        response.numberOfGroups++;
      }
    }
    return response;
  }, [stats, currencyEnabled, currency]);

  return (
    <Container>
      {loading ? (
        <EmptyContainer>
          <Ring color={"white"} size={30} />
        </EmptyContainer>
      ) : (
        <>
          <CashCancellationsCourtesy
            totalSales={
              typeof data.totalSales === "number"
                ? formatMoneyMK(data.totalSales)
                : "N/A"
            }
            salesPerGuest={
              typeof data.salesPerGuest === "number"
                ? formatMoney(data.salesPerGuest / data.numberOfGroups)
                : "N/A"
            }
            cashPercentage={
              typeof data.cashTotal === "number" &&
              typeof data.totalSales === "number"
                ? data.cashTotal > 0
                  ? formatPercentage((data.cashTotal * 100) / data.totalSales)
                  : "0%"
                : "N/A"
            }
            cancellationPercentage={
              typeof data.cancellationsTotal === "number" &&
              typeof data.totalSales === "number"
                ? data.cancellationsTotal > 0
                  ? formatPercentage(
                      (data.cancellationsTotal * 100) / data.totalSales
                    )
                  : "0%"
                : "N/A"
            }
            courtesyPercentage={
              typeof data.courtesyTotal === "number" &&
              typeof data.totalSales === "number"
                ? data.courtesyTotal > 0
                  ? formatPercentage(
                      (data.courtesyTotal * 100) / data.totalSales
                    )
                  : "0%"
                : "N/A"
            }
          />
        </>
      )}
    </Container>
  );
};

export default Day;
